import {
    ButtonGradientPrimary,
    Card,
    Colors,
    FIXED_ICON_SIZES,
    Heading,
    Icon,
    Text,
} from '@amelio/ui-library';
import { useState } from 'react';

import { ObjectivePeriodRow } from './ObjectivePeriodRow';

import type { ObjectivePeriod, ObjectivePeriodRequest } from '@/types/objective-periods';

import {
    useCreateObjectivePeriod,
    useGetObjectivePeriods,
    useUpdateObjectivePeriod,
} from '@/api/objective-periods';
import AddEditObjectivePeriodModal from '@/components/AddEditObjectivePeriodModal/AddEditObjectivePeriodModal';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { showSuccessToast } from '@/plugins/Toast';
import {
    cycleCardRadius,
    cycleCreateButtonHeight,
    cycleSectionIconFrame,
    cycleSectionIconSize,
} from '@/utils/cycleStyles';

const ObjectivePeriodsPanelHeader = () => {
    const { t } = useAmelioTranslations();

    return (
        <div className={'flex min-w-0 items-start gap-[12px]'}>
            <div
                className={`bg-base-50 flex shrink-0 items-center justify-center shadow-sm ${cycleSectionIconFrame} rounded-lg`}
                aria-hidden={'true'}
            >
                <Icon
                    name={'time-calendar'}
                    className={cycleSectionIconSize}
                    color={Colors.PRIMARY}
                />
            </div>

            <div className={'flex min-w-0 flex-col gap-1.5'}>
                <Heading
                    level={3}
                    weight={'semibold'}
                >
                    {t('objectivePeriods.title')}
                </Heading>

                <Text
                    size={'small'}
                    color={Colors.BASE_500}
                    className={'wrap-break-word'}
                >
                    {t('objectivePeriods.description')}
                </Text>
            </div>
        </div>
    );
};

export const ObjectivePeriods = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const { data: periods = [], isLoading, error } = useGetObjectivePeriods();
    const createObjectivePeriod = useCreateObjectivePeriod();
    const updateObjectivePeriod = useUpdateObjectivePeriod();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPeriod, setEditingPeriod] = useState<ObjectivePeriod | undefined>();

    const handleAddPeriod = () => {
        setEditingPeriod(undefined);
        setIsModalOpen(true);
    };

    const handleEditPeriod = (period: ObjectivePeriod) => {
        setEditingPeriod(period);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingPeriod(undefined);
    };

    const handleSavePeriod = async (request: ObjectivePeriodRequest) => {
        try {
            if (editingPeriod) {
                await updateObjectivePeriod.mutateAsync({ id: editingPeriod.id, request });
            } else {
                await createObjectivePeriod.mutateAsync(request);
            }
            handleCloseModal();
            showSuccessToast({ message: t('objectivePeriods.saveSuccess') });
        } catch (saveError) {
            handleError({ error: saveError });
        }
    };

    const addPeriodButton = (
        <ButtonGradientPrimary
            size={'md'}
            onClick={handleAddPeriod}
            className={`flex items-center self-start ${cycleCreateButtonHeight}`}
            dataTestId={'add-objective-period-button'}
            leftIcon={
                <Icon
                    name={'essential-add-circle'}
                    color={Colors.WHITE}
                    className={'mr-2'}
                    size={FIXED_ICON_SIZES.md}
                />
            }
        >
            {t('objectivePeriods.addPeriod')}
        </ButtonGradientPrimary>
    );

    const renderContent = () => {
        if (isLoading) {
            return (
                <div data-testid={'objective-periods-loader'}>
                    <Skeleton className={'flex flex-col gap-10'}>
                        <Bone className={'h-25 w-full'} />

                        <Bone className={'h-25 w-full'} />

                        <Bone className={'h-25 w-full'} />
                    </Skeleton>
                </div>
            );
        }

        if (error) {
            return (
                <div
                    role={'alert'}
                    data-testid={'objective-periods-error'}
                >
                    <Text color={Colors.ERROR_800}>
                        {t('objectivePeriods.errorLoadingPeriods')}
                    </Text>
                </div>
            );
        }

        return (
            <div className={'flex flex-col max-md:gap-[16px] md:gap-[24px]'}>
                {periods.length === 0 ? (
                    <div data-testid={'objective-periods-empty-state'}>
                        <Text>{t('objectivePeriods.noPeriods')}</Text>
                    </div>
                ) : (
                    periods.map((period) => (
                        <ObjectivePeriodRow
                            key={period.id}
                            period={period}
                            onEdit={() => handleEditPeriod(period)}
                        />
                    ))
                )}

                {addPeriodButton}
            </div>
        );
    };

    return (
        <Card
            padding={'none'}
            className={`relative flex flex-col justify-center max-md:gap-[16px] max-md:p-[16px] md:gap-[24px] md:p-[48px] ${cycleCardRadius}`}
            borderColor={Colors.WHITE}
            dataTestId={'objective-periods-panel'}
        >
            <ObjectivePeriodsPanelHeader />

            {renderContent()}

            <AddEditObjectivePeriodModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSavePeriod}
                period={editingPeriod}
                existingPeriods={periods}
                isSaving={createObjectivePeriod.isPending || updateObjectivePeriod.isPending}
            />
        </Card>
    );
};
